import { CreditsPerMonth } from 'constants/credits';
import React, { useState, useMemo } from 'react';
import {
    Interval,
    SampleCatalog,
    BundleCatalog,
    SaleDto,
} from '@landr/pricing/lib/types/generated';
import { CONFIG } from 'utils/config';
import { getReturnUrl } from 'helpers';
import { GetDiscountedPlan } from 'hooks/useGetDiscountedPlan/useGetDiscountedPlan';
import getYearlySaving from '../helpers/getYearlySaving';

const useUpsellActionsProvider = ({
    creditsNeeded = 0,
    samplesCatalog,
    studioCatalog,
    catalogSale,
    getDiscountedPlan,
}: {
    creditsNeeded?: number;
    samplesCatalog?: SampleCatalog;
    studioCatalog?: BundleCatalog;
    catalogSale?: SaleDto;
    getDiscountedPlan: GetDiscountedPlan;
}) => {
    const showFrequencySwitch = creditsNeeded <= CreditsPerMonth;
    const [selectedPlan, setSelectedPlan] = useState<string>('');
    const [showYearly, setShowYearly] = useState(true);

    const frequency = showYearly
        ? ('Yearly' as Interval)
        : ('Monthly' as Interval);

    const handleFrequencyChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setShowYearly(event.target.checked);
    };

    const handlePlanSelect = (planCode: string) => {
        setSelectedPlan(planCode);
    };

    const endDate = catalogSale?.endDate;
    const saleTitle = catalogSale?.prismicBanner?.title;
    const saleDesc = catalogSale?.prismicBanner?.description;

    const hasSale =
        !!getDiscountedPlan({ planCode: 'all_access', frequency: 'Yearly' }) ||
        !!getDiscountedPlan({ planCode: 'samples_pro', frequency: 'Yearly' });

    // Studio yearly offer is more advantageous, we only calculate the yearly saving on that plan
    const studioYearlySaving = getYearlySaving(studioCatalog?.plans);
    const samplesYearlySaving = getYearlySaving(samplesCatalog?.plans);

    const yearlySaving = Math.max(studioYearlySaving, samplesYearlySaving);

    const purchaseLink = useMemo(() => {
        if (!selectedPlan || !frequency) {
            return '';
        }

        const discountedPlan = getDiscountedPlan({
            planCode: selectedPlan,
            frequency,
        });
        const validCouponCode = discountedPlan ? discountedPlan.couponCode : '';

        return `${
            CONFIG.VITE_PURCHASE_URL
        }/${selectedPlan}/${frequency}/${validCouponCode}?returnUrl=${getReturnUrl()}`;
    }, [frequency, selectedPlan, getDiscountedPlan]);

    return {
        endDate,
        frequency,
        hasSale,
        saleTitle,
        saleDesc,
        purchaseLink,
        samplesCatalog,
        selectedPlan,
        showFrequencySwitch,
        showYearly,
        studioCatalog,
        yearlySaving,
        getDiscountedPlan,
        handleFrequencyChange,
        handlePlanSelect,
    };
};

export default useUpsellActionsProvider;
